// "Import data" modal for the data-table smart object. Educator-first: three
// input paths that all land in the same preview →
//   1. paste cells straight from Excel / Google Sheets (tab-separated grids;
//      falls back to full CSV parsing for comma text)
//   2. pick a .csv file (same parser, read via FileReader)
//   3. pick a .xlsx file — the "xlsx" package is LAZY-imported only when an
//      .xlsx is actually chosen, so it never weighs down the editor bundle
//
// One switch ("First row is column headers", default ON), a preview of the
// first rows, then Confirm hands { columns, rows } back to the sidebar which
// writes them into the DRAFT — the educator still presses the normal Apply.
//
// Rides the shared .cce-modal-overlay/.cce-modal pattern (SaveDesignModal,
// SmartObjectListField's item modal); import-specific styles are cce-so-import-*
// in SmartObjectsSidebar.css. All parse failures resolve to a friendly inline
// message — nothing here throws at the user.
import React, { useMemo, useRef, useState } from 'react';
import {
  cleanGrid,
  gridToTableData,
  parseClipboardGrid,
  parseCsv,
} from './SmartObjectImportUtils';

const PREVIEW_ROWS = 8;

/** Read a File via FileReader, resolving to text or an ArrayBuffer. */
const readFile = (file, as) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('The file could not be read.'));
  if (as === 'arrayBuffer') reader.readAsArrayBuffer(file);
  else reader.readAsText(file);
});

export default function SmartObjectImportModal({ maxCols, maxRows, onConfirm, onClose }) {
  const [pasteText, setPasteText] = useState('');
  // Grid parsed from a chosen FILE (takes precedence over the paste box until
  // the educator types/pastes again). { name, grid } | null.
  const [fileData, setFileData] = useState(null);
  const [firstRowHeaders, setFirstRowHeaders] = useState(true);
  const [error, setError] = useState('');
  const [reading, setReading] = useState(false);
  const fileInputRef = useRef(null);

  const activeGrid = useMemo(() => {
    if (fileData) return fileData.grid;
    return parseClipboardGrid(pasteText);
  }, [fileData, pasteText]);

  const table = useMemo(
    () => gridToTableData(activeGrid, { firstRowHeaders, maxCols, maxRows }),
    [activeGrid, firstRowHeaders, maxCols, maxRows],
  );

  const handlePasteChange = (text) => {
    setPasteText(text);
    setFileData(null); // typing/pasting takes over from any chosen file
    setError('');
  };

  const handleFile = async (file) => {
    if (!file) return;
    setReading(true);
    setError('');
    try {
      const name = file.name || 'file';
      const lower = name.toLowerCase();
      let grid;
      if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) {
        // Lazy: the xlsx library loads only on this path, only on demand.
        const mod = await import('xlsx');
        const XLSX = mod?.read ? mod : mod?.default;
        const buffer = await readFile(file, 'arrayBuffer');
        const workbook = XLSX.read(buffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        if (!firstSheet) throw new Error('That workbook has no sheets.');
        // header:1 → array-of-arrays; raw:false keeps dates/numbers as the
        // formatted text the educator sees in Excel; defval keeps row shapes.
        grid = XLSX.utils.sheet_to_json(firstSheet, { header: 1, raw: false, defval: '' });
      } else {
        const text = await readFile(file, 'text');
        grid = parseCsv(String(text ?? ''));
      }
      const clean = cleanGrid(grid);
      if (clean.length === 0) {
        setFileData(null);
        setError(`"${name}" looks empty — no rows with data were found.`);
      } else {
        setFileData({ name, grid: clean });
        setPasteText('');
      }
    } catch (err) {
      console.error('Table import failed:', err);
      setFileData(null);
      setError(`Sorry, that file could not be read as a table. ${err?.message || ''}`.trim());
    } finally {
      setReading(false);
      // Allow re-choosing the SAME file after a fix (input change won't fire
      // for an identical path otherwise).
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const truncateNote = table && (table.truncatedCols > 0 || table.truncatedRows > 0)
    ? [
      table.truncatedCols > 0 ? `only the first ${maxCols} columns` : null,
      table.truncatedRows > 0 ? `only the first ${maxRows} data rows` : null,
    ].filter(Boolean).join(' and ')
    : '';

  const previewRows = table ? table.rows.slice(0, PREVIEW_ROWS) : [];
  const hiddenRows = table ? table.rows.length - previewRows.length : 0;

  return (
    <div
      className="cce-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="cce-modal cce-so-import-modal">
        <h2 className="cce-modal-title cce-so-item-modal-title">Import table data</h2>

        <div className="cce-so-import-body">
          <div className="cce-so-field">
            <label className="cce-so-label" htmlFor="cce-so-import-paste">
              Paste cells from Excel or Google Sheets
            </label>
            <textarea
              id="cce-so-import-paste"
              className="cce-so-input cce-so-textarea cce-so-import-paste"
              rows={5}
              placeholder={'Copy a range of cells in your spreadsheet, then paste it here.\nPlain CSV text works too.'}
              value={pasteText}
              onChange={(e) => handlePasteChange(e.target.value)}
            />
          </div>

          <div className="cce-so-import-or">or</div>

          <div className="cce-so-field">
            <button
              type="button"
              className="cce-so-import-file-btn"
              disabled={reading}
              onClick={() => fileInputRef.current?.click()}
            >
              {reading ? 'Reading file…' : 'Choose a .csv or .xlsx file'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: 'none' }}
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
            {fileData && (
              <p className="cce-so-help">Loaded from “{fileData.name}”. Paste above to start over.</p>
            )}
          </div>

          <label className="cce-so-checkbox-row cce-so-import-headers">
            <input
              type="checkbox"
              checked={firstRowHeaders}
              onChange={(e) => setFirstRowHeaders(e.target.checked)}
            />
            <span>First row is column headers</span>
          </label>

          {error && <div className="cce-so-import-error">{error}</div>}

          {table ? (
            <div className="cce-so-import-preview">
              <div className="cce-so-import-preview-scroll">
                <table className="cce-so-import-table">
                  <thead>
                    <tr>
                      {table.columns.map((col, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <th key={i}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((row, ri) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <tr key={ri}>
                        {row.map((cell, ci) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <td key={ci}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="cce-so-import-summary">
                {table.columns.length} column{table.columns.length === 1 ? '' : 's'} · {table.rows.length} row{table.rows.length === 1 ? '' : 's'}
                {hiddenRows > 0 && ` (previewing the first ${PREVIEW_ROWS})`}
                {truncateNote && ` — this table fits ${truncateNote}, the rest will be left out`}
              </p>
            </div>
          ) : (
            !error && (
              <div className="cce-so-import-empty">
                Your data will be previewed here.
              </div>
            )
          )}
        </div>

        <div className="cce-so-import-actions">
          <button type="button" className="cce-so-import-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="cce-so-apply-btn cce-so-import-confirm"
            disabled={!table}
            onClick={() => table && onConfirm({ columns: table.columns, rows: table.rows })}
          >
            Use this data
          </button>
        </div>
      </div>
    </div>
  );
}
